'use client'

import { useMemo, useState } from 'react'

import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'
import { masterScheduleSummary, schedule } from '@/content/world-cup-2026/data'
import type { MasterMatch } from '@/content/world-cup-2026/types'

import { WcMatch } from './wc-match'

interface DateGroup {
    date: string
    matches: MasterMatch[]
}

/** Group consecutive matches by their (already date-ordered) date. */
const groupByDate = (matches: MasterMatch[]): DateGroup[] => {
    const groups: DateGroup[] = []
    for (const match of matches) {
        const last = groups.at(-1)
        if (last?.date === match.date) {
            last.matches.push(match)
        } else {
            groups.push({ date: match.date, matches: [match] })
        }
    }
    return groups
}

const uniqueInOrder = (values: string[]): string[] => [...new Set(values)]

const selectClasses =
    'h-10 rounded-[3px] border border-[var(--wc-smoke-500)] bg-white px-[11px] text-sm text-[var(--wc-fg)] outline-none focus:border-[var(--wc-blu-700)] focus:shadow-[0_0_0_2px_var(--wc-blu-300)]'

/**
 * Collapsible "all US group-stage games" table with host-city, group, and team
 * filters. Client component — the filtering and open/close state run in the
 * browser; the full table is rendered server-side first so it stays crawlable.
 */
export const WcMasterSchedule = () => {
    const [open, setOpen] = useState(false)
    const [city, setCity] = useState('')
    const [group, setGroup] = useState('')
    const [team, setTeam] = useState('')

    const cities = useMemo(() => uniqueInOrder(schedule.map((m) => m.stadiumLabel)).sort(), [])
    const groups = useMemo(() => uniqueInOrder(schedule.map((m) => m.group)).sort(), [])

    const matchesFilter = (m: MasterMatch): boolean => {
        const teamQuery = team.trim().toLowerCase()
        if (city && m.stadiumLabel !== city) {
            return false
        }
        if (group && m.group !== group) {
            return false
        }
        if (teamQuery && !`${m.home} ${m.away}`.toLowerCase().includes(teamQuery)) {
            return false
        }
        return true
    }

    const dateGroups = useMemo(() => groupByDate(schedule), [])
    const visibleGroups = dateGroups
        .map((g) => ({ date: g.date, matches: g.matches.filter(matchesFilter) }))
        .filter((g) => g.matches.length > 0)
    const hasResults = visibleGroups.length > 0

    const clear = () => {
        setCity('')
        setGroup('')
        setTeam('')
    }

    return (
        <section className="mb-[52px]">
            <details
                className="overflow-hidden rounded-lg shadow-[var(--wc-shadow-1)]"
                onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
            >
                <summary className="flex cursor-pointer list-none items-center justify-between bg-[var(--wc-blu-650)] px-5 py-4 text-[15px] font-bold text-white [&::-webkit-details-marker]:hidden">
                    <span>{masterScheduleSummary}</span>
                    <span className="inline-flex items-center gap-[7px] text-[13px] font-semibold text-[var(--wc-blu-100)]">
                        <ChevronDown
                            className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
                            aria-hidden="true"
                        />
                        {open ? 'Hide matches' : 'Show all matches'}
                    </span>
                </summary>

                <div className="flex flex-wrap items-end gap-3 border border-t-0 border-[var(--wc-border)] bg-white px-4 py-[14px]">
                    <label className="flex flex-col gap-[5px]">
                        <span className="text-[10px] font-bold tracking-[0.08em] text-[var(--wc-fg-3)] uppercase">
                            Host city
                        </span>
                        <select
                            aria-label="Host city"
                            className={selectClasses}
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                        >
                            <option value="">All cities</option>
                            {cities.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="flex flex-col gap-[5px]">
                        <span className="text-[10px] font-bold tracking-[0.08em] text-[var(--wc-fg-3)] uppercase">
                            Group
                        </span>
                        <select
                            aria-label="Group"
                            className={selectClasses}
                            value={group}
                            onChange={(e) => setGroup(e.target.value)}
                        >
                            <option value="">All groups</option>
                            {groups.map((g) => (
                                <option key={g} value={g}>
                                    {g}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="flex min-w-[170px] flex-1 flex-col gap-[5px]">
                        <span className="text-[10px] font-bold tracking-[0.08em] text-[var(--wc-fg-3)] uppercase">
                            Team
                        </span>
                        <input
                            aria-label="Team"
                            type="text"
                            placeholder="Search a team…"
                            className={cn(selectClasses, 'w-full')}
                            value={team}
                            onChange={(e) => setTeam(e.target.value)}
                        />
                    </label>

                    <button
                        type="button"
                        onClick={clear}
                        className="h-10 cursor-pointer rounded-[3px] border border-[var(--wc-border-dark)] bg-transparent px-[14px] text-[13px] font-bold text-[var(--wc-blu-700)] hover:bg-[var(--wc-blu-25)]"
                    >
                        Clear
                    </button>
                </div>

                <div className="overflow-hidden border border-t-0 border-[var(--wc-border)]">
                    {hasResults ? (
                        <table className="w-full border-collapse text-sm">
                            <caption className="sr-only">
                                All US group-stage matches, grouped by date
                            </caption>
                            <tbody>
                                {visibleGroups.map((g) => (
                                    <DateRows key={g.date} group={g} />
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p
                            data-testid="ms-empty"
                            className="px-4 py-[22px] text-center text-sm text-[var(--wc-fg-3)]"
                        >
                            No matches fit those filters. Try broader filters.
                        </p>
                    )}
                </div>
            </details>
        </section>
    )
}

const DateRows = ({ group }: { group: DateGroup }) => (
    <>
        <tr>
            <td
                colSpan={3}
                className="border-b border-[var(--wc-border)] bg-[var(--wc-smoke-50)] px-4 py-[9px] text-xs font-bold tracking-[0.04em] text-[var(--wc-fg-2)] uppercase"
            >
                {group.date}
            </td>
        </tr>
        {group.matches.map((m, i) => (
            <tr
                key={`${m.home}-${m.away}-${i}`}
                data-testid="ms-match-row"
                className="hover:bg-[var(--wc-blu-25)]"
            >
                <td className="border-b border-[var(--wc-border-light)] px-4 py-[11px] align-middle font-medium">
                    <WcMatch home={m.home} away={m.away} />
                </td>
                <td className="border-b border-[var(--wc-border-light)] px-4 py-[11px] align-middle text-xs text-[var(--wc-fg-3)]">
                    {m.group}
                </td>
                <td className="border-b border-[var(--wc-border-light)] px-4 py-[11px] text-right align-middle text-xs font-semibold">
                    <a href={`#${m.stadiumAnchor}`} className="text-[var(--wc-blu-700)]">
                        {m.stadiumLabel}
                    </a>
                </td>
            </tr>
        ))}
    </>
)
